  import React, { useState, useEffect } from 'react';
  import { motion, useAnimation, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion';
  import { Helmet } from 'react-helmet-async';
  import { useInView } from 'react-intersection-observer';
  import { Link } from 'react-router-dom';
  import { Menu, X, Code, Palette, MapPin, ExternalLink } from 'lucide-react';
  import projects from './data/projects';
 import LoginModal from './LoginModal';

  // --- Reusable Reveal Component ---
  const RevealSection = ({ children, id }) => {
    const controls = useAnimation();
    const [ref, inView] = useInView({ threshold: 0.15, triggerOnce: true });

    useEffect(() => {
      if (inView) controls.start("visible");
    }, [controls, inView]);

    return (
      <motion.section
        id={id} ref={ref}
        initial="hidden" animate={controls}
        variants={{ 
          hidden: { opacity: 0, y: 50 }, 
          visible: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } }
        }}
        className="py-32 px-6 md:px-12 max-w-7xl mx-auto w-full"
      >
        {children}
      </motion.section>
    );
  };

  const ObrianBaxterLogo = ({ className = "" }) => (
    <motion.div 
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 400, damping: 10 }}
      className={`inline-block cursor-pointer ${className}`}
    >
      <svg width="300" height="40" viewBox="0 0 520 70" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
        {/* Circle Graphic */}
        <circle cx="35" cy="35" r="15" stroke="#00f3ff" strokeWidth="12" />
        
        {/* OBRIAN */}
        <text x="57" y="46" fill="currentColor" style={{ font: '800 32px Inter, sans-serif', letterSpacing: '-0.02em' }} className="uppercase">
          'BRIAN
        </text>
        
        {/* BAXTER */}
        <text x="162" y="46" fill="currentColor" style={{ font: '300 32px Inter, sans-serif', letterSpacing: '0.01em' }} className="uppercase">
          BAXTER
        </text>

        {/* Architect - Now part of the SVG flow */}
        <text x="305" y="46" fill="currentColor" style={{ font: '500 32px Inter, sans-serif', letterSpacing: '-0.02em' }} className="uppercase">
          Architect
        </text>
      </svg>
    </motion.div>
  );

  // --- Legal Modal Component ---
  const LegalModal = ({ isOpen, onClose, title, content }) => (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-black/90 backdrop-blur-xl"
          onClick={onClose}
        >
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
            className="bg-[#111] border border-white/10 p-8 md:p-16 max-w-2xl w-full max-h-[80vh] overflow-y-auto relative"
            onClick={e => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute top-6 right-6 text-zinc-500 hover:text-[#00f3ff]"><X size={24} /></button>
            <h3 className="text-3xl font-black uppercase tracking-tighter mb-8 text-[#00f3ff]">{title}</h3>
            <div className="text-zinc-400 text-sm leading-relaxed space-y-4 font-light tracking-wide">
              {content}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  function App() {
    const [modalContent, setModalContent] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [result, setResult] = useState("");
    const [status, setStatus] = useState(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [cursorVariant, setCursorVariant] = useState("default");
    const currentYear = new Date().getFullYear();

    const { scrollY, scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });
    const y1 = useTransform(scrollY, [0, 500], [0, 200]);

    const portfolioItems = projects;

    useEffect(() => {
      const handleScroll = () => setIsScrolled(window.scrollY > 50);
      const mouseMove = (e) => setMousePos({ x: e.clientX, y: e.clientY });

      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('mousemove', mouseMove);

      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('mousemove', mouseMove);
      };
    }, []);

    const cursorVariants = {
      default: { 
        x: mousePos.x - 16, 
        y: mousePos.y - 16, 
        backgroundColor: "transparent", 
        border: "1px solid #00f3ff" 
      },
      pointer: { 
        x: mousePos.x - 40, 
        y: mousePos.y - 40, 
        height: 80, 
        width: 80, 
        backgroundColor: "#00f3ff", 
        mixBlendMode: "difference",
        border: "none"
      }
    };

    const handleSubmit = async (event) => {
      event.preventDefault();
      setResult("Processing...");
      const formData = new FormData(event.target);
      const object = Object.fromEntries(formData);
      
      try {
        const response = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(object)
        });
        const data = await response.json();
        if (data.success) {
          setStatus("success");
          setResult("Transmission Received.");
          event.target.reset();
        } else {
          setStatus("error");
          setResult("Error: " + data.message);
        }
      } catch (e) {
        setStatus("error");
        setResult("Connection Failed.");
      }
    };

    return (
      <div className="bg-[#0a0a0a] text-[#f5f5f5] min-h-screen font-sans selection:bg-[#00f3ff] selection:text-black overflow-x-hidden cursor-none">
        <Helmet>
          <title>O'Brian Baxter — Digital Architect</title>
          <meta name="description" content="Twin Cities digital architecture focused on React systems, UI/UX design, and strategic growth." />
          <meta name="theme-color" content="#00f3ff" />
        </Helmet>
        
        {/* Custom Magnetic Cursor */}
        <motion.div 
          className="fixed top-0 left-0 w-8 h-8 rounded-full z-[999] pointer-events-none hidden md:flex items-center justify-center overflow-hidden"
          variants={cursorVariants}
          animate={cursorVariant}
          transition={{ type: "spring", stiffness: 500, damping: 28, mass: 0.5 }}
        >
          {cursorVariant === "pointer" && (
            <span className="text-black text-[10px] font-black tracking-widest"></span>
          )}
        </motion.div>

        <motion.div 
          className="fixed top-0 left-0 right-0 h-[3px] bg-[#00f3ff] z-[110] origin-left" 
          style={{ scaleX }} 
        />

        <header className={`fixed top-0 w-full z-[100] transition-all duration-700 ${isScrolled ? 'bg-black/90 backdrop-blur-xl py-4 border-b border-white/5' : 'bg-transparent py-10'}`}>
          <div className="max-w-7xl mx-auto px-6 flex justify-between items-center w-full">
            <motion.h1 
              onMouseEnter={() => setCursorVariant("pointer")} 
              onMouseLeave={() => setCursorVariant("default")}
              className="text-xl font-black uppercase tracking-tighter"
            >
              O'Brian Baxter <span className="text-[#00f3ff] font-medium ml-2 hidden sm:inline">Architect</span>
            </motion.h1>
            
            <nav className="hidden md:flex items-center space-x-12 text-[10px] uppercase tracking-[0.4em] font-bold">
              <a href="#about" onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} className="hover:text-[#00f3ff] transition-colors">Legacy</a>
              <a href="#portfolio" onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} className="hover:text-[#00f3ff] transition-colors">Portfolio</a>
              <a href="https://www.linkedin.com/in/obrian-baxter/" target="_blank" rel="noopener noreferrer" onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} className="hover:text-[#00f3ff] transition-colors">LinkedIn</a>
              <a href="#contact" onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} className="px-6 py-3 bg-white text-black hover:bg-[#00f3ff] transition-all font-black">Inquire</a>
            </nav>

            <button onClick={() => setIsMenuOpen(true)} className="md:hidden p-2 text-[#00f3ff]" aria-label="Menu">
              <Menu size={24} />
            </button>
          </div>
        </header>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed inset-0 bg-black z-[200] p-12 flex flex-col justify-between"
            >
              <button onClick={() => setIsMenuOpen(false)} className="self-end p-4 text-[#00f3ff]"><X size={40} /></button>
              <div className="flex flex-col space-y-6">
                <a href="#about" onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} onClick={() => setIsMenuOpen(false)} className="text-5xl font-black uppercase tracking-tighter italic text-whitesmoke hover:text-[#00f3ff]">About</a>
                <a href="#portfolio" onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} onClick={() => setIsMenuOpen(false)} className="text-5xl font-black uppercase tracking-tighter italic text-whitesmoke hover:text-[#00f3ff]">Portfolio</a>
                <a href="https://www.linkedin.com/in/obrian-baxter/" onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} className="text-5xl font-black uppercase tracking-tighter italic text-whitesmoke hover:text-[#00f3ff]">LinkedIn</a>
                <a href="#contact" onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} onClick={() => setIsMenuOpen(false)} className="text-5xl font-black uppercase tracking-tighter italic text-whitesmoke hover:text-[#00f3ff]">Contact</a>
              </div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500">Roseville, Minnesota</div>
            </motion.div>
          )}
        </AnimatePresence>

        <main>
          <section className="relative h-[110vh] flex flex-col justify-center items-center text-center px-6 overflow-hidden">
            <motion.div style={{ y: y1 }} className="z-10">
              <h2 className="text-7xl md:text-[13rem] font-black uppercase tracking-tighter leading-[0.75] mb-8 font-montserrat">
                Digital<br/><span className="text-[#00f3ff]">Architect</span>
              </h2>
              <div className="flex items-center justify-center gap-2 text-zinc-500 uppercase tracking-widest text-[10px]">
                <MapPin size={14} className="text-[#00f3ff]" /> <span>Roseville, Minnesota</span>
              </div>
            </motion.div>
            <div className="absolute inset-0 flex items-center justify-center opacity-[0.02] pointer-events-none">
              <h2 className="text-[40vw] font-black uppercase leading-none">2004</h2>
            </div>
          </section>

          <RevealSection id="about">
            <div className="grid lg:grid-cols-12 gap-16 items-start">
              <div className="lg:col-span-7 space-y-10">
                <h3 className="text-5xl md:text-7xl font-bold uppercase leading-none font-montserrat">
                  Twenty Years <br/><span className="text-[#00f3ff]">At The Edge</span> <br/>Of Design.
                </h3>
                <p className="text-zinc-400 text-xl leading-relaxed max-w-2xl">
                  Operating from the technical heart of <span className="text-white">Roseville, MN</span>. I engineer digital ecosystems that balance aesthetic audacity with surgical precision for clients throughout the Twin Cities and beyond.
                </p>
              </div>
              <div className="lg:col-span-5 grid gap-4">
                <div onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} className="p-10 bg-zinc-900/30 border border-white/5 rounded-2xl hover:border-[#00f3ff]/30 transition-all">
                  <Code className="mb-6 text-[#00f3ff]" size={32} />
                  <h4 className="text-xl font-bold uppercase mb-2">Technical Mastery</h4>
                  <p className="text-zinc-500 text-sm">Scalable React architecture designed for high-performance enterprise applications.</p>
                </div>
                <div onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} className="p-10 bg-zinc-900/30 border border-white/5 rounded-2xl hover:border-[#00f3ff]/30 transition-all">
                  <Palette className="mb-6 text-[#00f3ff]" size={32} />
                  <h4 className="text-xl font-bold uppercase mb-2">Aesthetic Soul</h4>
                  <p className="text-zinc-500 text-sm">Minimalist design systems that command attention and define brands.</p>
                </div>
              </div>
            </div>
          </RevealSection>

          {/* Portfolio Section */}
          <RevealSection id="portfolio">
            <div className="mb-24 space-y-6">
              <h3 className="text-5xl md:text-8xl font-black uppercase tracking-tighter">Selected <br/><span className="text-[#00f3ff]">Systems.</span></h3>
              <p className="text-zinc-500 text-xs uppercase tracking-[0.5em] font-bold">Bridging Local Precision & International Scale</p>
            </div>

            <div className="grid md:grid-cols-2 gap-x-12 gap-y-32">
              {portfolioItems.map((item, index) => (
                <motion.div 
                  key={index}
                  whileHover={{ y: -10 }}
                  onMouseEnter={() => setCursorVariant("pointer")}
                  onMouseLeave={() => setCursorVariant("default")}
                  className={`group relative ${index % 2 !== 0 ? 'md:mt-32' : ''}`}
                >
                  <a href={item.link} target="_blank" rel="noopener noreferrer" className="block cursor-pointer">
                    <div className="relative overflow-hidden aspect-[4/5] bg-zinc-900 mb-8 border border-white/5 group-hover:border-[#00f3ff]/50 transition-colors">
                      <motion.img 
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.6 }}
                        src={item.image} 
                        alt={item.title} 
                        className="w-full h-full object-cover opacity-60 group-hover:opacity-100 grayscale group-hover:grayscale-0 transition-all duration-700"
                      />
                      <div className="absolute top-6 left-6 px-4 py-2 bg-black/80 backdrop-blur-md text-[#00f3ff] text-[10px] font-black uppercase tracking-widest border border-[#00f3ff]/20">
                        {item.tag}
                      </div>
                    </div>
                  </a>
                  <div className="space-y-4">
                    <div className="flex flex-col md:flex-row justify-between items-start gap-2">
                      <Link to={`/case-study/${item.slug}`} className="hover:text-[#00f3ff] transition-colors">
                        <h4 className="text-3xl font-black uppercase tracking-tighter">{item.title}</h4>
                        <p className="text-[#00f3ff] text-[10px] uppercase tracking-[0.3em] font-bold mt-1">{item.location}</p>
                      </Link>
                      <div className="flex items-center gap-3">
                        <a href={item.link} target="_blank" rel="noopener noreferrer" onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")}>View Live</a>
                        <a href={item.link} target="_blank" rel="noopener noreferrer">
                          <ExternalLink size={20} className="text-zinc-700 group-hover:text-[#00f3ff] transition-colors" />
                        </a>
                      </div>
                    </div>
                    <p className="text-zinc-500 text-sm leading-relaxed font-light border-l border-white/10 pl-6">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </RevealSection>

          <RevealSection id="contact">
            <div className="grid lg:grid-cols-2 gap-20">
              <div>
                <h3 className="text-5xl font-bold uppercase mb-6">Start your <br/><span className="text-[#00f3ff]">Project.</span></h3>
                <p className="text-zinc-500 mb-12">Available for senior consultation and full-stack development.</p>
                <div className="space-y-4 text-xs uppercase tracking-[0.3em]">
                  <p className="text-white">Roseville, MN 55113</p>
                  <p className="text-zinc-500 mt-4 font-bold tracking-widest text-sm">612.465.9196</p>
                  <p className="text-zinc-600 italic">Office • Saint Paul Metro</p>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <input type="hidden" name="access_key" value="5e8ea7f6-1e63-4fa9-985d-9f84ec97885d" />
                <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />
                <div className="space-y-4">
                  <input type="text" name="name" required placeholder="Name" className="w-full bg-zinc-900/50 border-b border-white/10 p-6 focus:border-[#00f3ff] outline-none transition-all" />
                  <input type="email" name="email" required placeholder="Email" className="w-full bg-zinc-900/50 border-b border-white/10 p-6 focus:border-[#00f3ff] outline-none transition-all" />
                  <textarea name="message" required placeholder="Project Vision" rows="4" className="w-full bg-zinc-900/50 border-b border-white/10 p-6 focus:border-[#00f3ff] outline-none transition-all" />
                </div>
                <button 
                  type="submit" 
                  onMouseEnter={() => setCursorVariant("pointer")} 
                  onMouseLeave={() => setCursorVariant("default")}
                  className="w-full bg-white text-black py-8 font-black uppercase hover:bg-[#00f3ff] transition-all tracking-[0.2em]"
                >
                  Submit Inquiry
                </button>
                <AnimatePresence>
                  {result && (
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`text-center text-[10px] uppercase font-bold tracking-[0.3em] ${status === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                      {result}
                    </motion.p>
                  )}
                </AnimatePresence>
              </form>
            </div>
          </RevealSection>
        </main>

        <footer className="py-24 border-t border-white/5 text-center px-6">
        <div className="flex justify-center gap-8 mb-8 text-[9px] uppercase tracking-[0.4em] text-zinc-500 font-bold">
          <button onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} onClick={() => setModalContent('privacy')} className="hover:text-[#00f3ff] transition-colors uppercase">Privacy</button>
          <button onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} onClick={() => setModalContent('terms')} className="hover:text-[#00f3ff] transition-colors uppercase">Terms</button>
          <button onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} onClick={() => setIsLoginModalOpen(true)} className="hover:text-[#00f3ff] transition-colors uppercase">Admin</button>
        </div>
        
        {/* New Logo Instance */}
        <div className="flex justify-center mb-12">
          <ObrianBaxterLogo className="text-zinc-600 hover:text-white transition-colors duration-300 w-64" />
        </div>

        <p className="text-[10px] uppercase tracking-[0.8em] opacity-30">&copy; {currentYear} O'Brian Baxter &bull; obrianbaxter.com</p>
      </footer>

        <LegalModal 
          isOpen={modalContent !== null} 
          onClose={() => setModalContent(null)}
          title={modalContent === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
          content={modalContent === 'privacy' ? (
            <>
              <p>Your privacy is paramount. As a Digital Architect based in Roseville, MN, I handle all client data with surgical precision.</p>
              <p><strong>Data Collection:</strong> We only collect information voluntarily submitted via our contact form (Name, Email, Project Details).</p>
              <p><strong>Usage:</strong> This data is used exclusively for project consultation. We do not use cookies for tracking or sell data to third parties.</p>
            </>
          ) : (
            <>
              <p>By accessing obrianbaxter.com, you agree to the following architectural standards.</p>
              <p><strong>Intellectual Property:</strong> All code snippets, visual designs, and case studies are the property of O'Brian Baxter.</p>
              <p><strong>Consultation:</strong> Inquiries made through this site do not constitute a binding development contract until a formal SOW is executed.</p>
            </>
          )}
        />

        <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      </div>
    );
  }

  export default App;